import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState, NotAvailableState } from "../components/States.js";
import { apiFetch, ApiRequestError, isNotAvailable } from "../lib/api.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { useToast } from "../components/Toast.js";
import { ScopeHeader } from "../components/ScopeHeader.js";
import { useMyGroups } from "../lib/scope.js";
import {
  MISSING_ROW_POLICY_LABELS,
  SURVIVOR_LABELS,
  WITHIN_BATCH_DUP_MODE_LABELS,
  WRITE_MODE_LABELS,
  writeModeDisplayLabel,
} from "../lib/pdoUiLabels.js";
import { LATE_ARRIVING_LABELS, MergeHistoryFieldPickers } from "../components/pdo/MergeHistoryFieldPickers.js";
import { PersistRunHistory } from "../components/pdo/PersistRunHistory.js";
import type { DupHandling } from "../components/pdo/WithinBatchDupSection.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// ADR-0094: 'merge' added; legacy `null_key_handling` removed; new shapes
// for change_detection and duplicate_key_handling.
type WriteMode = "replace_snapshot" | "append" | "merge" | "merge_history";
type PdoStatus = "active" | "draft" | "archived";
type StorageTier = "warm" | "cold";

type PdoConfig = {
  version: "pdo.v1";
  write_mode: WriteMode;
  business_key?: string[];
  business_time?: { source_field: string; fallback: "system_time" | "reject" };
  change_detection?: { untracked_fields: string[] };
  missing_row_policy?: "ignore_missing" | "close_on_missing";
  late_arriving_policy?: "reject" | "allow_monotonic_only";
  duplicate_key_handling?: DupHandling;
  retention?: { max_versions: number | null; max_age_days: number | null; cold_max_age_days: number | null };
  /** ADR-0103: keyed-mode rollback depth (current + N retained rollback points). */
  rollback_copies?: number;
};

type PdoVersion = {
  id: string;
  version_number: number;
  is_current: boolean;
  storage_tier: StorageTier;
  row_count: number;
  storage_bytes: number | null;
  write_mode: WriteMode;
  source_operation_id: string | null;
  source_node_id: string | null;
  created_at: string;
  // ADR-0092 — upload-source fields populated when source_kind='upload'.
  source_kind?: "upstream" | "upload";
  upload_original_filename?: string | null;
  upload_uploaded_by_account_name?: string | null;
  // ADR-0103: the watermark value this copy left off at (from its producing
  // materialization). Null for non-watermark PDOs. Shown in keyed rollback points.
  watermark_value_at_end?: string | null;
  // ADR-0103: rows_active = open records (excludes closed merge_history history).
  // Only populated for merge_history copies written after the active-count change.
  // restored_from_version is set on a rollback copy (the version it was restored from).
  merge_summary?: { rows_active?: number | null; restored_from_version?: number | null } | null;
};


type PdoDetail = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  status: PdoStatus;
  config: PdoConfig;
  // ADR-0103 (B): structural fields from the snapshot + operational fields
  // (retention, rollback_copies, policies) overlaid from the live spec — the
  // values that will actually run. Use this for read-only operational display.
  effective_config?: PdoConfig | null;
  tags: string[];
  owner_account_id: string | null;
  source_operation_id: string | null;
  source_node_id: string | null;
  source_operation_name: string | null;
  source_node_label: string | null;
  created_at: string;
  updated_at: string;
  // ADR-0068 scope fields (returned by GET /tenant/pdos/:id).
  scope?: import("../lib/scope.js").ContentScope;
  owner_group_id?: string | null;
  owner_group_name?: string | null;
  // ADR-0092 — source-type discriminator + upload metadata (defaults to
  // 'upstream' for legacy/graph PDOs returned by SELECT p.*).
  source_type?: "upstream" | "upload";
  upload_parser?: "xlsx" | "csv" | "tsv" | "json" | "jsonl" | "parquet" | null;
  upload_retention_policy?: "replace" | "snapshot" | null;
  upload_snapshot_max_versions?: number | null;
  current_version: {
    version_number: number;
    schema: { columns?: Array<{ name: string; type: string; nullable?: boolean }> } | null;
    row_count: number;
    storage_bytes: number | null;
    write_mode: WriteMode;
    merge_summary: unknown | null;
    created_at: string;
    source_kind?: "upstream" | "upload";
    upload_original_filename?: string | null;
    upload_uploaded_by_account_name?: string | null;
  } | null;
};

type PreviewResult = {
  columns: Array<{ name: string; type: string }>;
  rows: unknown[][];
  rowCount: number;
  previewRowCount: number;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtBytes(n: number | null): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}


function fmtRows(n: number | null): string {
  if (n == null) return "—";
  return n.toLocaleString();
}

/** Absolute timestamp (standing rule — never relative). */
function fmtTs(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

/**
 * Per-run row breakdown from a materialization's `summary` JSONB.
 * - read = `rows_read` (provider output count, stamped by the executor)
 * - inserted = `rows_new`
 * - updated = `rows_changed`
 * - ignored = `rows_unchanged` + `rows_skipped` (append-mode dupes)
 * All defensive: failed/running runs (null summary) and pre-rows_read
 * records return null/0.
 */
function previewCellValue(row: unknown, colIndex: number, colName: string): unknown {
  if (Array.isArray(row)) return row[colIndex];
  if (row && typeof row === "object" && colName in (row as object)) {
    return (row as Record<string, unknown>)[colName];
  }
  return undefined;
}

function formatPreviewCell(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

function normDesc(s: string | null | undefined): string | null {
  const t = (s ?? "").trim();
  return t.length > 0 ? t : null;
}

function parseTagsStr(s: string): string[] {
  return s.split(",").map((t) => t.trim()).filter(Boolean);
}

function tagsArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const x = [...a].sort();
  const y = [...b].sort();
  return x.every((v, i) => v === y[i]);
}

function descEq(a: string | null | undefined, b: string | null | undefined): boolean {
  return normDesc(a) === normDesc(b);
}

function fmtRetentionInt(n: number | null | undefined): string {
  if (n == null) return "—";
  return String(n);
}

function statusPillClass(status: PdoStatus): string {
  if (status === "active") return "pillOk";
  if (status === "draft") return "pillWarn";
  return "pillNeutral";
}


/**
 * ADR-0094: Ensure key columns don't appear in the change-detection deny-list.
 * Keys never participate in change detection, so listing a key in
 * `untracked_fields` is harmless — but normalize it out for clean diffs.
 */
function stripTrackedFieldsThatArePk(cfg: PdoConfig): PdoConfig {
  if (cfg.write_mode !== "merge_history" || !cfg.change_detection) return cfg;
  const pk = new Set(cfg.business_key ?? []);
  const prev = cfg.change_detection.untracked_fields ?? [];
  if (!prev.length) return cfg;
  const next = prev.filter((f) => !pk.has(f));
  if (next.length === prev.length) return cfg;
  return {
    ...cfg,
    change_detection: { ...cfg.change_detection, untracked_fields: next },
  };
}

/**
 * ADR-0094 §3.6: render a rejected row's data as a labeled key→value list,
 * never raw JSON (per feedback_never_show_json_to_users). Each rejected row
 * shows error_type as a pill, the engine's message, and an inline list of
 * column → value pairs from the offending input row.
 */
function defaultConfig(writeMode: WriteMode): PdoConfig {
  const base: PdoConfig = { version: "pdo.v1", write_mode: writeMode };
  // ADR-0094: keyed policies need a business_key + dup handling default.
  if (writeMode === "append" || writeMode === "merge") {
    return {
      ...base,
      business_key: [],
      duplicate_key_handling: { mode: "error" },
    };
  }
  if (writeMode === "merge_history") {
    return {
      ...base,
      business_key: [],
      business_time: { source_field: "", fallback: "system_time" },
      change_detection: { untracked_fields: [] },
      missing_row_policy: "ignore_missing",
      late_arriving_policy: "reject",
      duplicate_key_handling: { mode: "error" },
    };
  }
  return base;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PdoDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // Set by the operation editor's "View data store details" link — round-trip
  // back to the originating operation + persist node (same pattern as the
  // page→operation drill-in banner).
  const returnTo = searchParams.get("returnTo");
  const toast = useToast();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  /** ADR-0205 Amendment 3: opened by link, but this person may not use it. */
  const [notAvailable, setNotAvailable] = useState(false);
  const [pdo, setPdo] = useState<PdoDetail | null>(null);
  const [versions, setVersions] = useState<PdoVersion[]>([]);
  // ADR-0068 caller context for <ScopeHeader>
  const [callerAccountId, setCallerAccountId] = useState<string | null>(null);
  const [callerRole, setCallerRole] = useState<"admin" | "builder" | "viewer">("viewer");
  const { groups: callerGroups } = useMyGroups();
  const [saving, setSaving] = useState(false);
  /** Serialize PATCHes so rapid field changes don't produce concurrent requests. */
  const persistChainRef = useRef(Promise.resolve() as Promise<unknown>);
  /** Always-current snapshot read by the serialized save job at execution time. */
  const snapRef = useRef({
    pdo: null as PdoDetail | null,
    description: "",
    status: "draft" as PdoStatus,
    tagsStr: "",
    name: "",
    slug: "",
    config: { version: "pdo.v1" as const, write_mode: "replace_snapshot" as WriteMode },
    id: "",
  });
  /** Stable ref to the latest save() so the config-autosave effect doesn't capture a stale closure. */
  const saveRef = useRef<() => void>(() => {});

  // Editable field state
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<PdoStatus>("draft");
  const [tagsStr, setTagsStr] = useState("");
  const [config, setConfig] = useState<PdoConfig>({ version: "pdo.v1", write_mode: "replace_snapshot" });
  // ADR-0103 (B): effective (live) config for read-only operational display.
  const [effectiveConfig, setEffectiveConfig] = useState<PdoConfig | null>(null);

  // Version preview state
  const [previewVersionNumber, setPreviewVersionNumber] = useState<number | null>(null);
  const [previewData, setPreviewData] = useState<PreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [mergeUpstreamColumns, setMergeUpstreamColumns] = useState<string[]>([]);
  const [mergeColumnsLoading, setMergeColumnsLoading] = useState(false);
  const [mergeColumnsError, setMergeColumnsError] = useState<string | null>(null);

  const updatedAtRef = useRef<string>("");

  function applyPdo(d: PdoDetail) {
    setPdo(d);
    setName(d.name);
    setSlug(d.slug);
    setDescription(d.description ?? "");
    setStatus(d.status);
    setTagsStr(d.tags.join(", "));
    setConfig(stripTrackedFieldsThatArePk(d.config));
    setEffectiveConfig(d.effective_config ?? null);
    updatedAtRef.current = d.updated_at;
    setMergeUpstreamColumns([]);
    setMergeColumnsError(null);
  }

  const srcOpId = pdo?.source_operation_id ?? null;
  const srcNodeId = pdo?.source_node_id ?? null;

  const fetchMergeUpstreamColumns = useCallback(async () => {
    if (!srcOpId || !srcNodeId) return;
    setMergeColumnsLoading(true);
    setMergeColumnsError(null);
    try {
      // ADR-0120 §5.4: pure cache read — no chain execution (the old
      // execute-preview call force-ran the full chain just to learn columns).
      const data = await apiFetch<{
        ports?: Array<{ columns?: Array<{ name?: string }> | null }>;
        columns?: Array<{ name?: string }> | null;
      }>(
        `/api/tenant/operations/${encodeURIComponent(srcOpId)}/nodes/${encodeURIComponent(srcNodeId)}/upstream-columns`
      );
      const cols = Array.isArray(data.columns)
        ? data.columns
        : (data.ports ?? []).flatMap((p) => p.columns ?? []);
      const names = cols
        .map((c) => c.name)
        .filter((n): n is string => typeof n === "string" && n.trim().length > 0);
      setMergeUpstreamColumns(names);
    } catch (e) {
      setMergeColumnsError(e instanceof Error ? e.message : String(e));
    } finally {
      setMergeColumnsLoading(false);
    }
  }, [srcOpId, srcNodeId]);

  useEffect(() => {
    if (!pdo || pdo.source_operation_id || config.write_mode !== "merge_history") return;
    void fetchMergeUpstreamColumns();
  }, [pdo, config.write_mode, fetchMergeUpstreamColumns]);

  async function load() {
    setLoading(true);
    setFetchError(null);
    setNotAvailable(false);
    try {
      // Run history (materializations + per-run rejects) is owned by
      // <PersistRunHistory>, which fetches its own data — PdoDetail no longer
      // pre-fetches materializations or error counts here.
      const [detailData, versionsData, meData] = await Promise.all([
        apiFetch<{ pdo: PdoDetail }>(`/api/tenant/pdos/${encodeURIComponent(id)}`),
        apiFetch<{ versions: PdoVersion[] }>(`/api/tenant/pdos/${encodeURIComponent(id)}/versions`),
        apiFetch<{ user: { id: string; entitlement?: string } }>("/api/tenant/me").catch(() => null)
      ]);
      applyPdo(detailData.pdo);
      setVersions(versionsData.versions);
      if (meData) {
        setCallerAccountId(meData.user.id);
        const e = meData.user.entitlement;
        setCallerRole(e === "admin" ? "admin" : e === "builder" ? "builder" : "viewer");
      }
    } catch (e) {
      if (isNotAvailable(e)) setNotAvailable(true);
      else setFetchError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [id]);

  /**
   * Serialize all PATCHes through a promise chain so rapid changes (e.g. config
   * picker immediately followed by a status change) never produce concurrent
   * requests. Every save for a non-sourced PDO includes the full current state —
   * metadata *and* config — so there is no window where a metadata-only PATCH
   * can race with a pending config autosave and overwrite unsaved config changes.
   */
  function save() {
    persistChainRef.current = persistChainRef.current.then(async () => {
      const s = snapRef.current;
      const curPdo = s.pdo;
      if (!curPdo) return;

      const sourced = !!curPdo.source_operation_id;
      const effDesc = normDesc(s.description);
      const effStatus = s.status;
      const effTags = parseTagsStr(s.tagsStr);
      const effName = s.name.trim();
      const effSlug = s.slug.trim();
      const effConfig = sourced ? undefined : stripTrackedFieldsThatArePk(s.config);

      const metaUnchanged =
        descEq(effDesc, curPdo.description) &&
        effStatus === curPdo.status &&
        tagsArraysEqual(effTags, curPdo.tags) &&
        (sourced || (effName === curPdo.name && effSlug === curPdo.slug));
      const configUnchanged =
        sourced ||
        JSON.stringify(effConfig) === JSON.stringify(stripTrackedFieldsThatArePk(curPdo.config));
      if (metaUnchanged && configUnchanged) return;

      setSaving(true);
      try {
        const body: Record<string, unknown> = {
          description: effDesc,
          status: effStatus,
          tags: effTags,
          expected_updated_at: updatedAtRef.current,
        };
        if (!sourced) {
          body.name = effName;
          body.slug = effSlug;
          body.config = effConfig;
        }
        const data = await apiFetch<{ pdo: PdoDetail }>(
          `/api/tenant/pdos/${encodeURIComponent(s.id)}`,
          { method: "PATCH", body }
        );
        applyPdo(data.pdo);
      } catch (e) {
        if (e instanceof ApiRequestError && e.status === 409) {
          toast.push({ kind: "error", message: "This PDO was changed elsewhere. Reloading." });
          await load();
        } else {
          toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
        }
      } finally {
        setSaving(false);
      }
    }).catch(() => {});
  }

  saveRef.current = save;

  // Debounced auto-save for config picker changes (non-sourced PDOs only).
  // Because every save always includes the current config, a metadata save that
  // fires before this timer will also capture the pending config edit, making
  // the debounced save a cheap no-op.
  useEffect(() => {
    if (!pdo || pdo.source_operation_id) return;
    const t = setTimeout(() => saveRef.current(), 600);
    return () => clearTimeout(t);
  }, [config]); // intentionally omits pdo — we only want this on config changes

  async function deletePdo() {
    const ok = await confirm.confirm({
      title: "Delete this PDO?",
      detail: `This deletes "${name}" and all its versions. This cannot be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/tenant/pdos/${encodeURIComponent(id)}`, { method: "DELETE" });
      nav("/app/build/pdos");
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function previewVersion(versionNumber: number) {
    setPreviewVersionNumber(versionNumber);
    setPreviewData(null);
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const data = await apiFetch<{ preview: PreviewResult }>(
        `/api/tenant/pdos/${encodeURIComponent(id)}/versions/${versionNumber}/preview`
      );
      setPreviewData(data.preview);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewLoading(false);
    }
  }

  async function restoreVersion(versionNumber: number) {
    try {
      await apiFetch(
        `/api/tenant/pdos/${encodeURIComponent(id)}/versions/${versionNumber}/restore`,
        { method: "POST" }
      );
      toast.push({ kind: "success", message: `Version ${versionNumber} restored to warm storage` });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  // ADR-0103: keyed rollback — promote a retained rollback point to current via
  // the Phase 4 write-forward endpoint. version_number is the internal id and is
  // never shown; the user picks a copy by its capture timestamp.
  async function rollbackToVersion(versionNumber: number, capturedAt: string, watermarkValue: string | null) {
    const name = pdo?.name?.trim() || "this data store";
    const when = fmtTs(capturedAt);
    const base = `This replaces the current data of "${name}" with the copy captured ${when}. The current data becomes a rollback point. This can't be undone.`;
    // ADR-0103: for a watermark-bound store, the restore resets the cursor to the
    // value this copy left off at — surface it so the consequence is clear before
    // confirming.
    const detail = watermarkValue != null
      ? `${base} Incremental loading will resume from ${watermarkValue}.`
      : base;
    const ok = await confirm.confirm({
      title: "Restore rollback point",
      detail,
      confirmLabel: "Restore",
      destructive: true,
      requireTypedConfirmation: { value: name, label: "Type the data store name to confirm:" },
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/tenant/pdos/${encodeURIComponent(id)}/rollback`, {
        method: "POST",
        body: { to_version_number: versionNumber },
      });
      toast.push({ kind: "success", message: `Restored the copy from ${when}.` });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  function updateWriteMode(newMode: WriteMode) {
    setConfig(defaultConfig(newMode));
  }

  function updateConfig(patch: Partial<PdoConfig>) {
    setConfig((c) => ({ ...c, ...patch }));
  }

  function updateBizTime(patch: Partial<NonNullable<PdoConfig["business_time"]>>) {
    setConfig((c) => ({
      ...c,
      business_time: { ...c.business_time, source_field: "", fallback: "system_time", ...patch },
    }));
  }

  function updateChangeDetection(patch: Partial<NonNullable<PdoConfig["change_detection"]>>) {
    setConfig((c) => ({
      ...c,
      change_detection: { untracked_fields: [], ...c.change_detection, ...patch },
    }));
  }

  function updateRetention(patch: Partial<NonNullable<PdoConfig["retention"]>>) {
    setConfig((c) => ({
      ...c,
      retention: {
        max_versions: null,
        max_age_days: null,
        cold_max_age_days: null,
        ...c.retention,
        ...patch,
      },
    }));
  }

  snapRef.current = { pdo, description, status, tagsStr, name, slug, config, id };

  if (loading) {
    return (
      <PageLayout title="Persistent Data Object">
        <LoadingState />
      </PageLayout>
    );
  }

  if (notAvailable) {
    return (
      <PageLayout title="Persistent Data Object">
        <NotAvailableState thing="persistent data object" />
      </PageLayout>
    );
  }

  if (fetchError || !pdo) {
    return (
      <PageLayout title="Persistent Data Object">
        <ErrorState detail={fetchError ?? "PDO not found"} />
      </PageLayout>
    );
  }

  const isMergeHistory = config.write_mode === "merge_history";
  const sourcedPdo = !!pdo.source_operation_id;
  // ADR-0103 (B): show the operational values that will actually run (live spec
  // overlay) rather than the frozen snapshot. Structural fields are identical in
  // both; operational fields (retention, rollback_copies, policies) read live.
  const displayConfig = effectiveConfig ?? config;
  // Deep-link back to the exact persist node (?node=...) so the round-trip
  // from the operation editor's "View data store details" returns the user
  // to the node they came from, not just the operation canvas.
  const designerHref =
    pdo.source_operation_id != null
      ? `/app/build/operations/${pdo.source_operation_id}${
          pdo.source_node_id ? `?node=${encodeURIComponent(pdo.source_node_id)}` : ""
        }`
      : null;

  return (
    <PageLayout
      title={
        <span>
          <Link to="/app/build/pdos" style={{ color: "inherit", textDecoration: "none", opacity: 0.6 }}>
            Persistent Data Objects
          </Link>
          <span style={{ opacity: 0.4, margin: "0 6px" }}>/</span>
          <span>{pdo.name || pdo.slug}</span>
        </span>
      }
      actions={
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {saving ? <span className="muted" style={{ fontSize: 12 }}>Saving…</span> : null}
          <button className="btn btnDanger" type="button" onClick={() => void deletePdo()}>
            Delete
          </button>
        </div>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {/* Round-trip banner back to the originating operation + persist node
            (same pattern as the page→operation drill-in). Only when arrived
            via the operation editor's "View data store details" link. */}
        {returnTo ? (
          <div className="returnToPageBanner">
            <button
              type="button"
              className="btn btnLink returnToPageLink"
              onClick={() => nav(returnTo)}
            >
              ← Back to {pdo.source_operation_name ?? "operation"}
            </button>
            <span className="returnToPageHint">Navigating away from this page will clear this link.</span>
          </div>
        ) : null}
        {/* ADR-0068 scope header. No Clone — PDOs are pipeline-driven. */}
        {pdo.scope && (
          <ScopeHeader
            entityKind="pdo"
            entityId={pdo.id}
            scope={pdo.scope}
            ownerGroupId={pdo.owner_group_id ?? null}
            ownerGroupName={pdo.owner_group_name ?? null}
            ownerAccountId={pdo.owner_account_id}
            callerAccountId={callerAccountId}
            callerRole={callerRole}
            callerGroupIds={callerGroups.map((g) => g.id)}
            onScopeChanged={() => void load()}
            disableClone
          />
        )}

        {/* Source */}
        {pdo.source_operation_id ? (
          <div className="card">
            <div style={{ fontWeight: 600, marginBottom: 12 }}>Source</div>
            <p className="muted" style={{ fontSize: 12, margin: "0 0 12px" }}>
              Configure the persist node and run materialization from the operation designer.
            </p>
            <div style={{ display: "grid", gap: 8, fontSize: 13 }}>
              {pdo.source_node_label ? (
                <div className="field">
                  <label className="label">Persist node</label>
                  <span style={{ fontSize: 13 }}>{pdo.source_node_label}</span>
                </div>
              ) : null}
              <div className="field">
                <label className="label">Operation</label>
                {designerHref ? (
                  <Link to={designerHref} style={{ fontSize: 13 }}>
                    {pdo.source_operation_name ?? "Open operation designer"} →
                  </Link>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {/* General */}
        <div className="card">
          <div style={{ fontWeight: 600, marginBottom: 12 }}>General</div>
          <div style={{ display: "grid", gap: 10 }}>
            <div className="field">
              <label className="label">Name</label>
              <div style={{ fontSize: 13 }}>{name}</div>
              <span className="help">Follows the operation and persist-node names automatically.</span>
            </div>
            <div className="field">
              <label className="label">Slug</label>
              <div className="mono" style={{ fontSize: 12, padding: "6px 10px", background: "var(--bg-subtle, #f5f5f5)", borderRadius: 6, display: "inline-block" }}>
                {slug}
              </div>
            </div>
            <div className="field">
              <label className="label" htmlFor="pdo-desc">Description</label>
              <textarea
                id="pdo-desc"
                className="input reportTextarea"
                value={description}
                disabled={saving}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={() => { if (!saving) save(); }}
                rows={2}
              />
            </div>
            <div className="field">
              <label className="label" htmlFor="pdo-status">Status</label>
              <select
                id="pdo-status"
                className="select"
                value={status}
                disabled={saving}
                onChange={(e) => {
                  const next = e.target.value as PdoStatus;
                  if (next === status || saving) return;
                  setStatus(next);
                  save();
                }}
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="pdo-tags">Tags</label>
              <input
                id="pdo-tags"
                className="input"
                value={tagsStr}
                disabled={saving}
                onChange={(e) => setTagsStr(e.target.value)}
                onBlur={() => { if (!saving) save(); }}
                placeholder="tag1, tag2, tag3"
              />
              <span className="help">Comma-separated</span>
            </div>
          </div>
        </div>

        {sourcedPdo ? (
          <>
            <div className="card">
              <div style={{ fontWeight: 600, marginBottom: 12 }}>Write configuration</div>
              <p className="muted" style={{ fontSize: 12, margin: "0 0 12px" }}>
                Write mode, merge keys, policies, and retention are edited on the persist node in the operation designer.
              </p>
              <div style={{ display: "grid", gap: 10 }}>
                <div className="field">
                  <label className="label">Write mode</label>
                  <div style={{ fontSize: 13 }}>{WRITE_MODE_LABELS[config.write_mode]}</div>
                </div>
                {isMergeHistory ? (
                  <>
                    <div className="field">
                      <label className="label">Primary key columns</label>
                      <div className="mono" style={{ fontSize: 12 }}>
                        {(config.business_key ?? []).length ? (config.business_key ?? []).join(", ") : "—"}
                      </div>
                    </div>
                    <div className="field">
                      <label className="label">Business event time</label>
                      <div style={{ fontSize: 13 }}>
                        {(config.business_time?.source_field ?? "").trim() || "—"}
                        <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
                          fallback: {config.business_time?.fallback === "reject" ? "Reject" : "System time"}
                        </span>
                      </div>
                    </div>
                    <div className="field">
                      <label className="label">Change detection</label>
                      <div style={{ fontSize: 13 }}>
                        {(() => {
                          const untracked = displayConfig.change_detection?.untracked_fields ?? [];
                          if (untracked.length === 0) return "Hash (all non-key columns)";
                          return (
                            <>
                              Hash, excluding:
                              <div className="mono" style={{ fontSize: 12, marginTop: 4 }}>
                                {untracked.join(", ")}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="field">
                      <label className="label">Missing row policy</label>
                      <div style={{ fontSize: 13 }}>
                        {MISSING_ROW_POLICY_LABELS[displayConfig.missing_row_policy ?? "ignore_missing"]}
                      </div>
                    </div>
                    <div className="field">
                      <label className="label">Late-arriving rows</label>
                      <div style={{ fontSize: 13 }}>
                        {LATE_ARRIVING_LABELS[displayConfig.late_arriving_policy ?? "reject"]}
                      </div>
                    </div>
                    <div className="field">
                      <label className="label">Within-batch duplicates</label>
                      <div style={{ fontSize: 13 }}>
                        {(() => {
                          const dup = displayConfig.duplicate_key_handling ?? { mode: "error" };
                          if (dup.mode === "error") return WITHIN_BATCH_DUP_MODE_LABELS.error;
                          return `${WITHIN_BATCH_DUP_MODE_LABELS.keep_one} (${SURVIVOR_LABELS[dup.survivor]} by ${dup.tiebreaker_column})`;
                        })()}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
            <div className="card">
              <div style={{ fontWeight: 600, marginBottom: 12 }}>Retention</div>
              <div style={{ display: "grid", gap: 10 }}>
                {displayConfig.write_mode === "append" || displayConfig.write_mode === "merge" || displayConfig.write_mode === "merge_history" ? (
                  // ADR-0103: keyed stores keep one current copy + N rollback
                  // points — the warm/cold version knobs don't apply. Live value.
                  <div className="field">
                    <label className="label">Rollback copies</label>
                    <div style={{ fontSize: 13 }}>{displayConfig.rollback_copies ?? 0}</div>
                    <span className="help">Prior copies retained as restorable rollback points (the current copy is always kept). Applies on the next run — no re-initialize.</span>
                  </div>
                ) : (
                  <>
                    <div className="field">
                      <label className="label">Max warm versions</label>
                      <div style={{ fontSize: 13 }}>{fmtRetentionInt(displayConfig.retention?.max_versions ?? null)}</div>
                    </div>
                    <div className="field">
                      <label className="label">Max warm age (days)</label>
                      <div style={{ fontSize: 13 }}>{fmtRetentionInt(displayConfig.retention?.max_age_days ?? null)}</div>
                    </div>
                    <div className="field">
                      <label className="label">Cold retention (days)</label>
                      <div style={{ fontSize: 13 }}>{fmtRetentionInt(displayConfig.retention?.cold_max_age_days ?? null)}</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="card">
              <div style={{ fontWeight: 600, marginBottom: 12 }}>Write Configuration</div>
              <div style={{ display: "grid", gap: 10 }}>
                <div className="field">
                  <label className="label" htmlFor="pdo-write-mode">Write Mode</label>
                  <select
                    id="pdo-write-mode"
                    className="select"
                    value={config.write_mode}
                    onChange={(e) => updateWriteMode(e.target.value as WriteMode)}
                  >
                    <option value="replace_snapshot">{WRITE_MODE_LABELS.replace_snapshot}</option>
                    <option value="append">{WRITE_MODE_LABELS.append}</option>
                    <option value="merge">{WRITE_MODE_LABELS.merge}</option>
                    <option value="merge_history">{WRITE_MODE_LABELS.merge_history}</option>
                  </select>
                </div>

                {isMergeHistory ? (
                  <>
                    {!srcOpId || !srcNodeId ? (
                      <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                        This data store has no linked source operation, so column names cannot be loaded automatically. Set primary key and event time from the operation editor, or restore source metadata.
                      </p>
                    ) : null}
                    <MergeHistoryFieldPickers
                      columnNames={mergeUpstreamColumns}
                      columnsLoading={mergeColumnsLoading}
                      columnsError={mergeColumnsError}
                      onRefreshColumns={() => void fetchMergeUpstreamColumns()}
                      refreshDisabled={saving}
                      primaryKeyFields={[...(config.business_key ?? [])].sort((a, b) => a.localeCompare(b))}
                      onPrimaryKeyChange={(fields) => {
                        // ADR-0094: a key column can't appear in the deny-list.
                        const pk = new Set(fields);
                        const prevUntracked = config.change_detection?.untracked_fields ?? [];
                        const nextUntracked = prevUntracked.filter((f) => !pk.has(f));
                        updateConfig({
                          business_key: fields,
                          change_detection: { untracked_fields: nextUntracked },
                        });
                      }}
                      businessEventTimeField={config.business_time?.source_field ?? ""}
                      onBusinessEventTimeField={(field) => updateBizTime({ source_field: field })}
                      businessEventTimeFallback={config.business_time?.fallback ?? "system_time"}
                      onBusinessEventTimeFallback={(v) => updateBizTime({ fallback: v })}
                      untrackedFields={config.change_detection?.untracked_fields ?? []}
                      onUntrackedFieldsChange={(fields) =>
                        updateChangeDetection({ untracked_fields: fields })
                      }
                      disabled={saving}
                    />
                    <div className="field">
                      <label className="label" htmlFor="pdo-missing-row">Missing Row Policy</label>
                      <select
                        id="pdo-missing-row"
                        className="select"
                        value={config.missing_row_policy ?? "ignore_missing"}
                        onChange={(e) => updateConfig({ missing_row_policy: e.target.value as "ignore_missing" | "close_on_missing" })}
                      >
                        <option value="ignore_missing">{MISSING_ROW_POLICY_LABELS.ignore_missing}</option>
                        <option value="close_on_missing">{MISSING_ROW_POLICY_LABELS.close_on_missing}</option>
                      </select>
                    </div>
                    <div className="field">
                      <label className="label" htmlFor="pdo-late-arriving">Late-arriving rows</label>
                      <select
                        id="pdo-late-arriving"
                        className="select"
                        value={config.late_arriving_policy ?? "reject"}
                        onChange={(e) => updateConfig({ late_arriving_policy: e.target.value as "reject" | "allow_monotonic_only" })}
                      >
                        <option value="reject">{LATE_ARRIVING_LABELS.reject}</option>
                        <option value="allow_monotonic_only">{LATE_ARRIVING_LABELS.allow_monotonic_only}</option>
                      </select>
                    </div>
                    {/*
                      ADR-0094: Within-batch duplicate handling on PdoDetail is
                      display-only here. Editing the discriminated shape
                      (tiebreaker column + survivor rule) belongs in the
                      operation inspector — the PdoDetail config-edit form was
                      only reachable for upload-source PDOs anyway (graph-source
                      blocked by lockIdentity). Keep display only.
                     */}
                    <div className="field">
                      <label className="label">Within-batch duplicates</label>
                      <div style={{ fontSize: 13 }}>
                        {(() => {
                          const dup = config.duplicate_key_handling ?? { mode: "error" };
                          if (dup.mode === "error") return WITHIN_BATCH_DUP_MODE_LABELS.error;
                          return `${WITHIN_BATCH_DUP_MODE_LABELS.keep_one} (${SURVIVOR_LABELS[dup.survivor]} by ${dup.tiebreaker_column})`;
                        })()}
                      </div>
                      <div className="help" style={{ marginTop: 4 }}>
                        Configured at the operation node. Archive and recreate to change.
                      </div>
                    </div>
                    <div className="field">
                      <label className="label">NULL key handling</label>
                      <div className="help">
                        Rows with NULL values in any key column are always rejected.
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>

            <div className="card">
              <div style={{ fontWeight: 600, marginBottom: 12 }}>Retention</div>
              <div style={{ display: "grid", gap: 10 }}>
                <div className="field">
                  <label className="label" htmlFor="pdo-max-versions">Max Warm Versions</label>
                  <input
                    id="pdo-max-versions"
                    className="input"
                    type="number"
                    min={1}
                    value={config.retention?.max_versions ?? ""}
                    onChange={(e) =>
                      updateRetention({ max_versions: e.target.value ? parseInt(e.target.value, 10) : null })
                    }
                    placeholder="unlimited"
                  />
                </div>
                <div className="field">
                  <label className="label" htmlFor="pdo-max-age">Max Warm Age (Days)</label>
                  <input
                    id="pdo-max-age"
                    className="input"
                    type="number"
                    min={1}
                    value={config.retention?.max_age_days ?? ""}
                    onChange={(e) =>
                      updateRetention({ max_age_days: e.target.value ? parseInt(e.target.value, 10) : null })
                    }
                    placeholder="unlimited"
                  />
                </div>
                <div className="field">
                  <label className="label" htmlFor="pdo-cold-retention">Cold Retention (Days)</label>
                  <input
                    id="pdo-cold-retention"
                    className="input"
                    type="number"
                    min={1}
                    value={config.retention?.cold_max_age_days ?? ""}
                    onChange={(e) =>
                      updateRetention({ cold_max_age_days: e.target.value ? parseInt(e.target.value, 10) : null })
                    }
                    placeholder="keep forever"
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Schema — columns of the current copy, captured at materialization.
            Mirrors the provider inspector's Schema Contract table so provider
            output and stored PDO types can be compared side by side. */}
        {(() => {
          const cols = pdo?.current_version?.schema?.columns ?? [];
          if (cols.length === 0) return null;
          return (
            <div className="card">
              <div style={{ fontWeight: 600, marginBottom: 12 }}>Schema</div>
              <div className="tableWrap">
                <table className="table tableCompact">
                  <thead>
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Nullable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cols.map((c) => (
                      <tr key={c.name}>
                        <td className="mono" style={{ fontSize: 12 }}>{c.name}</td>
                        <td style={{ fontSize: 12 }}>{c.type}</td>
                        <td style={{ fontSize: 12 }}>{c.nullable === false ? "No" : "Yes"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="help" style={{ marginTop: 8 }}>
                Column types of the current stored copy, captured when it was last written.
              </div>
            </div>
          );
        })()}

        {/* Version History — ADR-0103: keyed stores show Current + Rollback
            points (no version numbers); snapshot keeps the numbered table. */}
        {(() => {
          const currentVersion = versions.find((v) => v.is_current) ?? null;
          const displayWriteMode = currentVersion?.write_mode ?? config.write_mode;
          const isKeyed = displayWriteMode === "append" || displayWriteMode === "merge" || displayWriteMode === "merge_history";
          // Current first, then rollback points by recency.
          const keyedCopies = [...versions].sort((a, b) => b.version_number - a.version_number);
          const showWmCol = versions.some((v) => v.watermark_value_at_end != null);
          // Active rows (open records) only differ from the total for merge_history,
          // which retains closed history rows.
          const showActiveCol = displayWriteMode === "merge_history";

          return (
            <div className="card">
              <div style={{ fontWeight: 600, marginBottom: 12 }}>{isKeyed ? "Data Store" : "Version History"}</div>
              {versions.length === 0 ? (
                <div className="adminListEmpty">No data yet. Materialize this data store to create the first copy.</div>
              ) : isKeyed ? (
                <div className="tableWrap">
                  {/* ADR-0103: one table for the keyed store — the current copy
                      plus any retained rollback points. table-layout: fixed →
                      columns share the width evenly. (Version numbers are shown
                      per user request, overriding the spec's original "no version
                      numbers for keyed".) */}
                  <table className="table tableCompact" style={{ tableLayout: "fixed" }}>
                    <thead>
                      {/* All columns left-aligned for a uniform grid — mixing a
                          right-aligned numeric column next to left-aligned text
                          left a jarring gap that read as broken formatting. */}
                      <tr>
                        <th>Version</th>
                        <th>Captured</th>
                        <th title="All stored rows, including closed history records">Total rows</th>
                        {showActiveCol ? <th title="Open records only (excludes closed history)">Active rows</th> : null}
                        {showWmCol ? <th title="The watermark value this copy left off at — a restore resumes from here">Watermark</th> : null}
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {keyedCopies.map((v) => {
                        // A rollback copy is the restored copy's data, so show the
                        // ORIGINAL capture date (the version it came from) and a
                        // "↩ from vN" marker — not the rollback timestamp.
                        const restoredFrom = v.merge_summary?.restored_from_version ?? null;
                        const sourceVersion = restoredFrom != null
                          ? versions.find((x) => x.version_number === restoredFrom) ?? null
                          : null;
                        const capturedAt = sourceVersion?.created_at ?? v.created_at;
                        return (
                        <tr key={v.id}>
                          <td className="mono" style={{ fontSize: 12 }}>
                            v{v.version_number}
                            {restoredFrom != null ? (
                              <span className="muted" style={{ marginLeft: 6, fontSize: 11 }} title={`Rolled back from v${restoredFrom}`}>↩ v{restoredFrom}</span>
                            ) : null}
                          </td>
                          <td style={{ fontSize: 12 }}>{fmtTs(capturedAt)}</td>
                          <td className="mono" style={{ fontSize: 12 }}>{fmtRows(v.row_count)}</td>
                          {showActiveCol ? (
                            <td className="mono" style={{ fontSize: 12 }}>
                              {typeof v.merge_summary?.rows_active === "number" ? fmtRows(v.merge_summary.rows_active) : "—"}
                            </td>
                          ) : null}
                          {showWmCol ? (
                            <td
                              className="mono"
                              style={{ fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              title={v.watermark_value_at_end ?? undefined}
                            >
                              {v.watermark_value_at_end ?? "—"}
                            </td>
                          ) : null}
                          <td>
                            {v.is_current ? (
                              <span className="pillOk">Current</span>
                            ) : (
                              <button
                                className="adminListAction"
                                type="button"
                                onClick={() => void rollbackToVersion(v.version_number, capturedAt, v.watermark_value_at_end ?? null)}
                              >
                                Restore
                              </button>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="tableWrap">
                  <table className="table tableCompact">
                    <thead>
                      <tr>
                        <th>Version</th>
                        <th>Tier</th>
                        <th>Rows</th>
                        <th>Size</th>
                        <th>Write Mode</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {versions.map((v) => (
                        <tr key={v.id}>
                          <td>
                            <span className="mono" style={{ fontSize: 12 }}>v{v.version_number}</span>
                            {v.is_current ? (
                              <span className="pillOk" style={{ marginLeft: 6, fontSize: 11 }}>current</span>
                            ) : null}
                          </td>
                          <td>
                            {v.storage_tier === "warm" ? (
                              <span className="pillOk">warm</span>
                            ) : (
                              <span className="pillNeutral">cold</span>
                            )}
                          </td>
                          <td>{fmtRows(v.row_count)}</td>
                          <td>{fmtBytes(v.storage_bytes)}</td>
                          <td style={{ fontSize: 12 }}>{writeModeDisplayLabel(v.write_mode)}</td>
                          <td style={{ fontSize: 12 }}>{fmtTs(v.created_at)}</td>
                          <td>
                            <div className="adminListActions">
                              {v.storage_tier === "warm" ? (
                                <button
                                  className="adminListAction"
                                  type="button"
                                  onClick={() => void previewVersion(v.version_number)}
                                >
                                  Preview
                                </button>
                              ) : (
                                <button
                                  className="adminListAction"
                                  type="button"
                                  onClick={() => void restoreVersion(v.version_number)}
                                >
                                  Restore
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

        {/* Data Preview */}
        {previewVersionNumber != null ? (
          <div className="card">
            <div style={{ fontWeight: 600, marginBottom: 12 }}>
              Data Preview — v{previewVersionNumber}
              <button
                className="btn btnSm"
                type="button"
                style={{ marginLeft: 12, fontWeight: 400 }}
                onClick={() => { setPreviewVersionNumber(null); setPreviewData(null); }}
              >
                Close
              </button>
            </div>
            {previewLoading ? <LoadingState /> : null}
            {!previewLoading && previewError ? <ErrorState detail={previewError} /> : null}
            {!previewLoading && previewData ? (
              <>
                {previewData.rowCount > 500 ? (
                  <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                    Showing first 500 of {previewData.rowCount.toLocaleString()} rows
                  </div>
                ) : null}
                <div className="tableWrap" style={{ maxHeight: 480, overflow: "auto" }}>
                  <table className="table tableCompact" style={{ width: "max-content", minWidth: "100%" }}>
                    <thead style={{ position: "sticky", top: 0, zIndex: 1, background: "var(--bg-elevated, #fff)" }}>
                      <tr>
                        {previewData.columns.map((c) => (
                          <th key={c.name} className="mono" style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                            {c.name}
                            <span className="muted" style={{ fontWeight: 400, marginLeft: 4 }}>({c.type})</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.rows.map((row, ri) => (
                        <tr key={ri}>
                          {previewData.columns.map((c, ci) => {
                            const cell = previewCellValue(row, ci, c.name);
                            return (
                              <td
                                key={c.name}
                                style={{ fontSize: 12, whiteSpace: "nowrap", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}
                                title={cell != null && typeof cell === "object" ? formatPreviewCell(cell) : undefined}
                              >
                                {cell == null ? (
                                  <span className="muted">null</span>
                                ) : (
                                  formatPreviewCell(cell)
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {/* ADR-0092 — Upload-source PDOs render an upload-friendly history table
            from the versions array (each upload IS a version; we don't bother
            with materialization rows for uploads). Graph-source PDOs keep the
            classic Materialization History table below. */}
        {pdo?.source_type === "upload" ? (
          <div className="card">
            <div style={{ fontWeight: 600, marginBottom: 12 }}>Upload history</div>
            {versions.length === 0 ? (
              <div className="adminListEmpty">No uploads yet.</div>
            ) : (
              <div className="tableWrap">
                <table className="table tableCompact">
                  <thead>
                    <tr>
                      <th>Version</th>
                      <th>File</th>
                      <th>Rows</th>
                      <th>Uploaded by</th>
                      <th>Uploaded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {versions.map((v) => (
                      <tr key={v.id}>
                        <td className="mono" style={{ fontSize: 12 }}>v{v.version_number}{v.is_current ? " (current)" : ""}</td>
                        <td>
                          {v.upload_original_filename ? (
                            <code className="mono" style={{ fontSize: 12 }}>{v.upload_original_filename}</code>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td style={{ fontSize: 12 }}>{fmtRows(v.row_count)}</td>
                        <td style={{ fontSize: 12 }}>{v.upload_uploaded_by_account_name ?? <span className="muted">—</span>}</td>
                        <td style={{ fontSize: 12 }}>{new Date(v.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
        <div className="card">
          <div style={{ fontWeight: 600, marginBottom: 12 }}>Run History</div>
          <PersistRunHistory
            pdoId={id}
            writeMode={config.write_mode}
            versions={versions}
            currentVersionNumber={pdo?.current_version?.version_number ?? null}
          />
        </div>
        )}

      </div>
    </PageLayout>
  );
}
